import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NotesIcon from "@mui/icons-material/Notes";
import SummarizeIcon from "@mui/icons-material/Summarize";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import { useParams } from "react-router-dom";

import type { RootState } from "../store/state/rootReducer";
import type { AppDispatch } from "../store/state/rootStore";
import { clearSelectedStories } from "../store/state/stories/actions";
import { useRoleFlags } from "../hooks/usePermissions";
import { AddToFoldersModal } from "../Pages/Project/Modals/AddToFoldersModal";
import {
  addBulkStoryToFolders,
  loadProjectStories,
  moveBulkStoriesToProject,
  resetStoriesProject,
  setProjectStoriesPageView,
} from "../store/actions/projects";
import { getFolder, getProjectFolders, resetFolderState } from "../store/actions/folders";
import { loadPortals } from "../store/actions/portals";
import { BulkDeleteModal } from "../Pages/Project/Modals/BulkDeleteModal";
import useMixPanel from "../lib/mixpanel";
import { BulkDownloadModal } from "../Pages/Project/Modals/BulkDownloadModal";
import { AutoIndexModalModal } from "../Pages/Project/Modals/AutoIndexModal";
import { GPTSummaryModal } from "../Pages/Project/Modals/GPTSummaryModal";
import { getEligibleAIStoryIds } from "../Pages/Project/Modals/aiGenerationUtils";
import { MoveToProjectModal } from "../Pages/Project/Modals/MoveToProjectModal";
import { AddStoryToPortalModal } from "../Pages/Portals/AddStoryToPortalModal";
import { SelectionActionBar, SelectionBarAction } from "../components/common/SelectionActionBar";

export const FooterActionBar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { project_id: projectId, folder_id: folderId } = useParams();
  const authenticated = useSelector((state: RootState) => state.auth.authenticated);
  const { isAdmin, isOrgManager, isProjectManager } = useRoleFlags();
  const selectedStories = useSelector((state: RootState) => state.stories.selectedStories);
  const selectedStoriesIds = selectedStories.map((story) => story._id);
  const projectFolders = useSelector((state: RootState) => state.folders?.projectFolders) ?? [];

  const [isBulkAddToFolderModalOpen, setIsBulkAddToFolderModalOpen] = useState(false);
  const [isDeleteStoriesModalOpen, setIsDeleteStoriesModalOpen] = useState(false);
  const [isDownloadStoriesModalOpen, setIsDownloadStoriesModalOpen] = useState(false);
  const [isBulkIndexModalOpen, setIsBulkIndexModalOpen] = useState(false);
  const [isBulkSummaryModalOpen, setIsBulkSummaryModalOpen] = useState(false);
  const [isMoveToProjectModalOpen, setIsMoveToProjectModalOpen] = useState(false);
  const [isAddToPortalModalOpen, setIsAddToPortalModalOpen] = useState(false);

  const hasSelectedStories = !!selectedStoriesIds?.length;
  const hasFolders = !!projectFolders?.length;
  const hasPermissionForBulkActions = isProjectManager || isAdmin;
  const canMoveStoriesToProject = (isOrgManager || isAdmin) && !!projectId;
  const userFlags = useSelector((state: RootState) => state.featureFlags?.flags);
  const portals = useSelector((state: RootState) => state.portals?.portals) ?? [];
  const isLoadingPortals = useSelector((state: RootState) => state.portals?.isLoadingPortals);
  const canAddToPortal = Boolean(userFlags?.enablePortals) && portals.some((portal) => portal.collections.length > 0);
  const eligibleAIStoryIds = getEligibleAIStoryIds(selectedStories);
  const hasEligibleAIStories = eligibleAIStoryIds.length > 0;
  const requestedPortalsRef = useRef(false);

  useEffect(() => {
    if (!authenticated) {
      requestedPortalsRef.current = false;
      return;
    }

    if (!userFlags?.enablePortals || isLoadingPortals || portals.length > 0 || requestedPortalsRef.current) return;

    requestedPortalsRef.current = true;
    dispatch(loadPortals());
  }, [authenticated, dispatch, isLoadingPortals, portals.length, userFlags?.enablePortals]);

  const onConfirmBulkAddToFolderHandler = async (selectedFoldersIds: string[]) => {
    useMixPanel("bulk_add_to_folder_stories", { stories: selectedStoriesIds });
    await dispatch(addBulkStoryToFolders(selectedStoriesIds, selectedFoldersIds));
    const isFolder = !!(folderId && projectId);

    if (isFolder) {
      dispatch(resetFolderState());
      dispatch(getFolder(folderId));
    } else {
      dispatch(resetStoriesProject());
      dispatch(setProjectStoriesPageView({ currentPage: 1 }));
      dispatch(loadProjectStories({ projectId }));
      dispatch(getProjectFolders(projectId));
    }
    dispatch(clearSelectedStories());
    setIsBulkAddToFolderModalOpen(false);
  };

  const onConfirmMoveToProjectHandler = async (targetProjectId: string) => {
    if (!projectId) {
      return;
    }

    useMixPanel("bulk_move_to_project", {
      stories: selectedStoriesIds,
      source_project_id: projectId,
      target_project_id: targetProjectId,
    });

    const didMove = await dispatch(moveBulkStoriesToProject(projectId, targetProjectId, selectedStoriesIds));
    if (!didMove) {
      return;
    }

    const isFolder = !!(folderId && projectId);

    if (isFolder) {
      dispatch(resetFolderState());
      dispatch(getFolder(folderId));
    } else {
      dispatch(resetStoriesProject());
      dispatch(setProjectStoriesPageView({ currentPage: 1 }));
      dispatch(loadProjectStories({ projectId }));
      dispatch(getProjectFolders(projectId));
    }

    dispatch(clearSelectedStories());
    setIsMoveToProjectModalOpen(false);
  };

  if (!hasSelectedStories) {
    return null;
  }

  return (
    <>
      <SelectionActionBar
        id="footer-action-bar"
        label={`${selectedStoriesIds.length} ${selectedStoriesIds.length > 1 ? "interviews" : "interview"} selected`}
        onCancel={() => dispatch(clearSelectedStories())}
      >
        {userFlags?.enableSpeechmaticsAutochaptering && hasPermissionForBulkActions && (
          <SelectionBarAction
            title={
              hasEligibleAIStories ? "Generate indexes" : "Selected interviews need completed transcript processing"
            }
            icon={NotesIcon}
            onClick={() => setIsBulkIndexModalOpen(true)}
            disabled={!hasEligibleAIStories}
          />
        )}
        {userFlags?.enableChatGpt && hasPermissionForBulkActions && (
          <SelectionBarAction
            title={
              hasEligibleAIStories ? "Generate summaries" : "Selected interviews need completed transcript processing"
            }
            icon={SummarizeIcon}
            onClick={() => setIsBulkSummaryModalOpen(true)}
            disabled={!hasEligibleAIStories}
          />
        )}
        {canMoveStoriesToProject && (
          <SelectionBarAction
            title="Move to project"
            icon={DriveFileMoveIcon}
            onClick={() => setIsMoveToProjectModalOpen(true)}
          />
        )}
        {hasFolders && hasPermissionForBulkActions && (
          <SelectionBarAction
            title="Manage folder(s)"
            icon={FolderIcon}
            onClick={() => setIsBulkAddToFolderModalOpen(true)}
          />
        )}
        {canAddToPortal && (
          <SelectionBarAction
            title="Add to portal"
            icon={WebAssetIcon}
            onClick={() => {
              dispatch(loadPortals());
              setIsAddToPortalModalOpen(true);
            }}
          />
        )}
        {hasPermissionForBulkActions && (
          <SelectionBarAction title="Delete" icon={DeleteIcon} onClick={() => setIsDeleteStoriesModalOpen(true)} />
        )}
        <SelectionBarAction
          title="Download"
          icon={FileDownloadIcon}
          onClick={() => setIsDownloadStoriesModalOpen(true)}
        />
      </SelectionActionBar>
      {isBulkAddToFolderModalOpen && (
        <AddToFoldersModal
          showModal={isBulkAddToFolderModalOpen}
          onCancel={() => setIsBulkAddToFolderModalOpen(false)}
          onConfirm={onConfirmBulkAddToFolderHandler}
        />
      )}
      {isDeleteStoriesModalOpen && (
        <BulkDeleteModal showModal={isDeleteStoriesModalOpen} onCancel={() => setIsDeleteStoriesModalOpen(false)} />
      )}
      {isMoveToProjectModalOpen && (
        <MoveToProjectModal
          showModal={isMoveToProjectModalOpen}
          onCancel={() => setIsMoveToProjectModalOpen(false)}
          onConfirm={onConfirmMoveToProjectHandler}
        />
      )}
      {isDownloadStoriesModalOpen && (
        <BulkDownloadModal
          showModal={isDownloadStoriesModalOpen}
          onCancel={() => {
            setIsDownloadStoriesModalOpen(false);
          }}
        />
      )}
      {isBulkIndexModalOpen && (
        <AutoIndexModalModal
          showModal={isBulkIndexModalOpen}
          onCancel={() => setIsBulkIndexModalOpen(false)}
          storyIds={selectedStoriesIds}
          stories={selectedStories}
          onComplete={() => dispatch(clearSelectedStories())}
        />
      )}
      {isBulkSummaryModalOpen && (
        <GPTSummaryModal
          showModal={isBulkSummaryModalOpen}
          onCancel={() => setIsBulkSummaryModalOpen(false)}
          storyIds={selectedStoriesIds}
          stories={selectedStories}
          onComplete={() => dispatch(clearSelectedStories())}
        />
      )}
      {isAddToPortalModalOpen && (
        <AddStoryToPortalModal
          open={isAddToPortalModalOpen}
          storyIds={selectedStoriesIds}
          onClose={() => setIsAddToPortalModalOpen(false)}
        />
      )}
    </>
  );
};
